/**
 * HUD - Heads Up Display for game information
 * Enhanced with traditional Eastern aesthetic UI design
 */

import { useGameStore } from '@/state/gameStore';
import { FACTIONS } from '@/game/factions/FactionData';
import { injectSharedStyles } from '@/ui/styles/UIStyles';
import { UI_IMAGES } from '@/ui/styles/UIIcons';

export class HUD {
  private container: HTMLElement;
  private goldDisplay: HTMLElement;
  private timeDisplay: HTMLElement;
  private unitDisplay: HTMLElement;
  private unsubscribe: (() => void) | null = null;

  constructor() {
    injectSharedStyles();

    this.container = document.createElement('div');
    this.container.id = 'hud';
    this.container.innerHTML = this.createHTML();

    this.goldDisplay = this.container.querySelector('#hud-gold')!;
    this.timeDisplay = this.container.querySelector('#hud-time')!;
    this.unitDisplay = this.container.querySelector('#hud-units')!;

    document.body.appendChild(this.container);

    this.setupSpeedControls();
    this.setupSubscription();
  }

  private createHTML(): string {
    return `
      <style>
        /* ===== HUD Container ===== */
        #hud {
          position: fixed;
          left: 16px;
          top: 16px;
          z-index: 10;
          font-family: 'Noto Serif SC', 'SimSun', serif;
          font-size: 14px;
          line-height: 1.4;
          color: #f5e6d3;
          user-select: none;
          width: 220px;
        }

        /* ===== Main Panel ===== */
        .hud-panel {
          background: linear-gradient(145deg, rgba(74,55,40,0.92) 0%, rgba(45,31,21,0.95) 100%);
          border: 3px solid #8b6914;
          border-radius: 8px;
          box-shadow:
            inset 0 2px 4px rgba(255,220,150,0.15),
            inset 0 -3px 6px rgba(0,0,0,0.4),
            0 6px 20px rgba(0,0,0,0.6);
          overflow: hidden;
          position: relative;
        }

        /* Corner Ornaments */
        .hud-corner {
          position: absolute;
          width: 36px;
          height: 36px;
          background-image: url('/images/ui/decorations/corner-ornaments.png');
          background-size: 200% 200%;
          z-index: 10;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));
          pointer-events: none;
        }
        .hud-corner.tl { top: -6px; left: -6px; background-position: 0% 0%; }
        .hud-corner.tr { top: -6px; right: -6px; background-position: 100% 0%; }
        .hud-corner.bl { bottom: -6px; left: -6px; background-position: 0% 100%; }
        .hud-corner.br { bottom: -6px; right: -6px; background-position: 100% 100%; }

        /* ===== Title Bar ===== */
        .hud-header {
          background: linear-gradient(180deg, rgba(107,68,35,0.95) 0%, rgba(74,47,23,0.98) 100%);
          border-bottom: 2px solid #8b6914;
          padding: 10px 14px;
          position: relative;
        }

        .hud-header::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 15%;
          right: 15%;
          height: 1px;
          background: linear-gradient(90deg,
            transparent,
            rgba(212,175,55,0.6) 30%,
            rgba(212,175,55,0.8) 50%,
            rgba(212,175,55,0.6) 70%,
            transparent
          );
        }

        .hud-header-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .hud-emblem {
          width: 42px;
          height: 42px;
          flex-shrink: 0;
          border-radius: 6px;
          overflow: hidden;
          background: rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hud-emblem img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
        }

        .hud-faction-name {
          flex: 1;
          font-size: 20px;
          font-weight: 700;
          color: #ffd35a;
          text-shadow:
            0 2px 4px rgba(0,0,0,0.6),
            0 0 12px rgba(255,180,0,0.3);
          letter-spacing: 2px;
        }

        /* ===== Stats Section ===== */
        .hud-stats {
          padding: 14px;
        }

        .hud-stat-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid rgba(139,105,20,0.2);
        }

        .hud-stat-row:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .hud-stat-row:first-child {
          padding-top: 0;
        }

        .hud-stat-label {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #c4a77d;
          font-size: 14px;
        }

        .hud-stat-icon {
          width: 26px;
          height: 26px;
          flex-shrink: 0;
        }

        .hud-stat-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .hud-stat-value {
          font-size: 24px;
          font-weight: 700;
          text-shadow: 0 2px 4px rgba(0,0,0,0.5);
          font-variant-numeric: tabular-nums;
        }

        .hud-stat-value.gold { color: #ffd700; }
        .hud-stat-value.units { color: #90caf9; }
        .hud-stat-value.territory { color: #81c784; }
        .hud-stat-value.time { color: #ffffff; }

        /* ===== Speed Controls ===== */
        .hud-speed {
          background: rgba(0,0,0,0.25);
          border-top: 1px solid rgba(139,105,20,0.4);
          padding: 10px 14px;
        }

        .hud-speed-buttons {
          display: flex;
          gap: 8px;
        }

        .hud-speed-btn {
          flex: 1;
          padding: 8px 0;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 2px solid #6b4423;
          border-radius: 4px;
          color: #c4a77d;
          font-size: 14px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.15s ease;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
        }

        .hud-speed-btn:hover {
          background: linear-gradient(180deg, #6b4e3a 0%, #4a3525 100%);
          border-color: #8b6914;
          color: #ffd35a;
        }

        .hud-speed-btn.active {
          background: linear-gradient(180deg, #8b6914 0%, #6b4423 100%);
          border-color: #d4af37;
          color: #fff;
          box-shadow: 0 0 8px rgba(212,175,55,0.4);
        }

        .hud-speed-btn.paused {
          background: linear-gradient(180deg, #8b2020 0%, #5c1515 100%);
          border-color: #c62828;
          color: #ffcdd2;
        }

        /* ===== Keyboard Shortcuts ===== */
        .hud-shortcuts {
          border-top: 1px solid rgba(139,105,20,0.3);
          padding: 12px 14px;
          font-size: 11px;
          color: #9e8b6e;
        }

        .hud-shortcuts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px 16px;
        }

        .hud-shortcut {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .hud-shortcut kbd {
          display: inline-block;
          min-width: 24px;
          padding: 2px 6px;
          background: rgba(0,0,0,0.35);
          border: 1px solid rgba(139,105,20,0.5);
          border-radius: 3px;
          color: #c4a77d;
          font-size: 10px;
          font-family: inherit;
          text-align: center;
        }

        .hud-shortcuts-divider {
          grid-column: span 2;
          height: 1px;
          margin: 6px 0;
          background: linear-gradient(90deg,
            transparent,
            rgba(139,105,20,0.3) 20%,
            rgba(139,105,20,0.3) 80%,
            transparent
          );
        }

        .hud-shortcuts-footer {
          grid-column: span 2;
          text-align: center;
          margin-top: 6px;
          font-size: 10px;
          color: #7a6b52;
        }

        /* Compact layout for small viewports (desktop 720p) */
        @media (max-height: 800px) {
          #hud {
            width: 180px;
            left: 10px;
            top: 10px;
          }
          .hud-panel {
            border-width: 2px;
          }
          .hud-corner {
            width: 28px;
            height: 28px;
          }
          .hud-header {
            padding: 6px 10px;
          }
          .hud-emblem {
            width: 32px;
            height: 32px;
          }
          .hud-faction-name {
            font-size: 16px;
          }
          .hud-stats {
            padding: 8px 10px;
          }
          .hud-stat-row {
            padding: 4px 0;
          }
          .hud-stat-label {
            font-size: 12px;
            gap: 6px;
          }
          .hud-stat-icon {
            width: 20px;
            height: 20px;
          }
          .hud-stat-value {
            font-size: 18px;
          }
          .hud-speed {
            padding: 6px 10px;
          }
          .hud-speed-buttons {
            gap: 4px;
          }
          .hud-speed-btn {
            padding: 5px 0;
            font-size: 12px;
          }
          .hud-shortcuts {
            padding: 8px 10px;
            font-size: 10px;
          }
          .hud-shortcuts-grid {
            gap: 3px 10px;
          }
          .hud-shortcut kbd {
            min-width: 20px;
            padding: 1px 4px;
            font-size: 9px;
          }
        }
      </style>

      <div class="hud-panel">
        <div class="hud-corner tl"></div>
        <div class="hud-corner tr"></div>
        <div class="hud-corner bl"></div>
        <div class="hud-corner br"></div>

        <!-- Header: Emblem + Faction Name -->
        <div class="hud-header">
          <div class="hud-header-content">
            <div class="hud-emblem">
              <img id="hud-faction-emblem" src="" alt="" />
            </div>
            <div class="hud-faction-name" id="hud-faction-name">-</div>
          </div>
        </div>

        <!-- Stats -->
        <div class="hud-stats">
          <div class="hud-stat-row">
            <span class="hud-stat-label">
              <span class="hud-stat-icon"><img src="${UI_IMAGES.icons.gold}" alt="" /></span>
              黄金
            </span>
            <span class="hud-stat-value gold" id="hud-gold">0</span>
          </div>
          <div class="hud-stat-row">
            <span class="hud-stat-label">
              <span class="hud-stat-icon"><img src="${UI_IMAGES.icons.unit}" alt="" /></span>
              单位
            </span>
            <span class="hud-stat-value units" id="hud-units">0</span>
          </div>
          <div class="hud-stat-row">
            <span class="hud-stat-label">
              <span class="hud-stat-icon"><img src="${UI_IMAGES.icons.territory}" alt="" /></span>
              领地
            </span>
            <span class="hud-stat-value territory" id="hud-territory">0</span>
          </div>
          <div class="hud-stat-row">
            <span class="hud-stat-label">
              <span class="hud-stat-icon"><img src="${UI_IMAGES.icons.time}" alt="" /></span>
              时间
            </span>
            <span class="hud-stat-value time" id="hud-time">0:00</span>
          </div>
        </div>

        <!-- Speed Controls -->
        <div class="hud-speed">
          <div class="hud-speed-buttons">
            <button class="hud-speed-btn" data-speed="0">II</button>
            <button class="hud-speed-btn" data-speed="1">1x</button>
            <button class="hud-speed-btn" data-speed="2">2x</button>
            <button class="hud-speed-btn" data-speed="3">3x</button>
          </div>
        </div>

        <!-- Keyboard Shortcuts -->
        <div class="hud-shortcuts">
          <div class="hud-shortcuts-grid">
            <div class="hud-shortcut"><kbd>空格</kbd> 暂停</div>
            <div class="hud-shortcut"><kbd>Esc</kbd> 菜单</div>
            <div class="hud-shortcut"><kbd>H</kbd> 帮助</div>
            <div class="hud-shortcut"><kbd>F</kbd> 势力</div>
            <div class="hud-shortcut"><kbd>R</kbd> 外交</div>
            <div class="hud-shortcut"><kbd>T</kbd> 科技</div>
            <div class="hud-shortcut"><kbd>O</kbd> 设置</div>
            <div class="hud-shortcuts-divider"></div>
            <div class="hud-shortcut"><kbd>F5</kbd> 存档</div>
            <div class="hud-shortcut"><kbd>F9</kbd> 读档</div>
            <div class="hud-shortcuts-footer">左键选择 · 右键指令</div>
          </div>
        </div>
      </div>
    `;
  }

  private setupSpeedControls(): void {
    const speedBtns = this.container.querySelectorAll('.hud-speed-btn');
    speedBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const speed = parseInt((btn as HTMLElement).dataset.speed || '1');
        useGameStore.getState().setGameSpeed(speed);
      });
    });
  }

  private setupSubscription(): void {
    this.unsubscribe = useGameStore.subscribe((state) => {
      this.updateDisplay(state);
    });
  }

  private updateDisplay(state: ReturnType<typeof useGameStore.getState>): void {
    // Update gold
    this.goldDisplay.textContent = state.playerGold.toLocaleString();

    // Update units
    this.unitDisplay.textContent = state.unitCount.toString();

    // Update territory
    const territoryDisplay = this.container.querySelector('#hud-territory');
    if (territoryDisplay) {
      territoryDisplay.textContent = state.territoryCount.toString();
    }

    // Update time
    const minutes = Math.floor(state.gameTime / 60);
    const seconds = Math.floor(state.gameTime % 60);
    this.timeDisplay.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    // Update faction name and emblem
    if (state.playerFaction) {
      const faction = FACTIONS[state.playerFaction];
      const nameEl = this.container.querySelector('#hud-faction-name');
      const emblemEl = this.container.querySelector('#hud-faction-emblem') as HTMLImageElement;

      if (nameEl) {
        nameEl.textContent = faction.name;
      }

      if (emblemEl) {
        emblemEl.src = `/images/emblems/${state.playerFaction}.webp`;
      }
    }

    // Update speed buttons
    this.updateSpeedButtons(state.gameSpeed);
  }

  private updateSpeedButtons(speed: number): void {
    const speedBtns = this.container.querySelectorAll('.hud-speed-btn');
    speedBtns.forEach((btn) => {
      const btnSpeed = parseInt((btn as HTMLElement).dataset.speed || '1');
      btn.classList.toggle('active', btnSpeed === speed && speed > 0);
      btn.classList.toggle('paused', btnSpeed === 0 && speed === 0);
    });
  }

  dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.container.remove();
  }
}
